import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, ExternalLink, Plus } from "lucide-react";
import { theme, card, emptyFrame, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { LoadingBlock } from "../../components/Spinner";
import { RulerProgress, StatusTag, type StatusTone } from "../../components/Drawing";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../../components/RecordView";
import { NextSteps } from "../../components/NextSteps";
import {
  TRAINING_KINDS, labelOf, ModalOverlay, ModalHeader, Field, Check, MultiPick, EmptyState, StatusPill,
  input, textarea, secondaryBtn, dangerBtn, fmtDay, toDateInput, dateInputToIso, fmtDateOnly, todayInput, tableHeadRow, type Option,
} from "./shared";
import { PhishingTrainingProposals, type ProposalAudience, type ProposalView } from "./PhishingTrainingProposals";
import { CompetenceTrainingProposals, type CompetenceProposalView } from "./CompetenceTrainingProposals";

/** What a proposal fills the assignment form with: phishing results or a competence gap. */
interface AssignmentSeed {
  heading: string;
  note: string;
  suggested: { title: string; kind: string; description: string; dueInDays: number; audience: ProposalAudience; competenceRequirementId?: string };
}

// Training assignments: courses assigned to an audience with a due date. Members complete them
// from My tasks, which writes the training record; an administrator-confirmed course waits here
// for a writer to confirm. Writes need controls:write (canEdit).

type Standing = "completed" | "excused" | "awaiting_confirmation" | "overdue" | "due_soon" | "pending";

interface Assignee {
  id: string;
  userId: string;
  cycle: number;
  dueAt: string;
  status: "pending" | "submitted" | "completed" | "excused";
  standing: Standing;
  submittedAt: string | null;
  completedAt: string | null;
  score: number | null;
  attestation: string | null;
  excusedReason: string | null;
  user: { id: string; displayName: string | null; email: string; isActive: boolean };
}

interface Assignment {
  id: string;
  displayId: number | null;
  title: string;
  kind: string;
  provider: string | null;
  url: string | null;
  description: string | null;
  autoEnrolJoiners: boolean;
  dueInDays: number;
  dueAt: string;
  recurrenceMonths: number | null;
  completionMode: "self_attest" | "admin_confirm";
  passMark: number | null;
  status: "active" | "closed";
  closedAt: string | null;
  createdAt: string;
  audienceLabel: string;
  assignees: Assignee[];
  progress: { expected: number; completed: number; overdue: number; awaitingConfirmation: number; rate: number | null };
}

interface Member { id: string; name: string; email: string }
interface Overview { assignments: Assignment[]; members: Member[]; departments: Option[]; sites: Option[] }

const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const headRow: React.CSSProperties = tableHeadRow;

const ref = (a: Assignment) => (a.displayId ? `TA-${String(a.displayId).padStart(3, "0")}` : "TA");
const modeLabel = (m: Assignment["completionMode"]) => (m === "admin_confirm" ? "Administrator confirms" : "Self-attest");
const recurrenceLabel = (n: number | null) => (n ? `every ${n} months` : "one-off");
const isHttps = (u: string | null): u is string => { try { return !!u && new URL(u).protocol === "https:"; } catch { return false; } };

function Pill({ tone, children, title }: { tone: "success" | "warning" | "danger" | "accent" | "muted"; children: React.ReactNode; title?: string }) {
  const s = {
    success: { bg: theme.color.successBg, color: theme.color.successText },
    warning: { bg: theme.color.warningBg, color: theme.color.warningText },
    danger:  { bg: theme.color.dangerBg,  color: theme.color.dangerText },
    accent:  { bg: theme.color.accentBg,  color: theme.color.accentText },
    muted:   { bg: theme.color.panelAlt,  color: theme.color.muted },
  }[tone];
  return <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{children}</span>;
}

const STANDING_PILL: Record<Standing, { tone: "success" | "warning" | "danger" | "accent" | "muted"; status?: StatusTone; label: string }> = {
  completed:             { tone: "success", status: "met",     label: "Completed" },
  excused:               { tone: "muted",                      label: "Excused" },
  awaiting_confirmation: { tone: "accent",  status: "partial", label: "To confirm" },
  overdue:               { tone: "danger",  status: "gap",     label: "Overdue" },
  due_soon:              { tone: "warning",                    label: "Due soon" },
  pending:               { tone: "muted",   status: "none",    label: "Not done yet" },
};

function ProgressBar({ rate }: { rate: number | null }) {
  return <RulerProgress value={rate ?? 0} label="Completion" style={{ minWidth: 80 }} />;
}

export function TrainingAssignments({ canEdit }: { canEdit: boolean }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Proposed from phishing results: the proposal the assignment form was opened from, if any.
  const [proposals, setProposals] = useState<ProposalView | null>(null);
  const [fromProposal, setFromProposal] = useState<AssignmentSeed | null>(null);
  const [competence, setCompetence] = useState<CompetenceProposalView | null>(null);
  const focusCompetence = params.get("competence");
  const openId = params.get("assignment");
  const focusProposal = params.get("proposal");

  const load = useCallback(async () => {
    try { setData(await api.get<Overview>("/training-assignments")); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load training assignments")); }
    // A proposal panel that fails to load leaves the rest of the page working.
    try { setProposals(await api.get<ProposalView>("/training-assignments/proposals")); }
    catch { setProposals(null); }
    // Only members who may assign training read competence proposals (they name people); anyone else gets none.
    try { setCompetence(await api.get<CompetenceProposalView>("/training-assignments/competence-proposals")); }
    catch { setCompetence(null); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function open(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set("assignment", id); else next.delete("assignment");
    setParams(next, { replace: true });
  }

  if (!data && !error) return <LoadingBlock />;
  const assignments = data?.assignments ?? [];
  const selected = openId ? assignments.find((a) => a.id === openId) ?? null : null;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ ...text.sectionTitle, margin: 0, display: "flex", alignItems: "center", gap: 6 }}><ClipboardList size={15} /> Assigned training</div>
          <div style={{ ...text.hint, marginTop: 2, maxWidth: 640 }}>
            Assign a course to everyone or to chosen departments, sites or people, with a due date. Each person records it from My tasks and it lands in the training records.
          </div>
        </div>
        {canEdit && data && <button type="button" style={btnPrimary} onClick={() => setCreating(true)}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Assign training</button>}
      </div>
      {error && <div style={{ ...text.body, ...card, padding: 12, color: theme.color.dangerText }}>{error}</div>}
      {notice && <div style={{ ...text.body, ...card, padding: 12, color: theme.color.successText }}>{notice}</div>}

      {proposals && data && (
        <PhishingTrainingProposals view={proposals} canEdit={canEdit} focusDepartmentId={focusProposal}
          onAssign={(p) => { setFromProposal({ heading: `Assign training to ${p.departmentName}`, note: "Proposed from phishing results. Check the course, due date and audience; nothing is assigned until you press Assign.", suggested: p.suggested }); setCreating(true); }}
          onChanged={async (msg) => { setNotice(msg); await load(); }} />
      )}
      {competence && data && (
        <CompetenceTrainingProposals view={competence} canEdit={canEdit} focusRequirementId={focusCompetence}
          onAssign={(p) => { setFromProposal({ heading: `Assign training for ${p.role}`, note: `Proposed from the competence register (${p.ref}). Assigning it answers the gap for the people it reaches; check the course, due date and people first.`, suggested: p.suggested }); setCreating(true); }}
          onChanged={async (msg) => { setNotice(msg); await load(); }} />
      )}

      {data && (assignments.length === 0 ? (
        <div style={{ ...emptyFrame, padding: 0 }}>
          <EmptyState title="No training assigned" detail="Assign your annual awareness course, an induction or role-specific training. People are emailed, reminded as the due date nears, and you see who has completed it (ISO 27001 7.2, 7.3, A.6.3)."
            action={canEdit ? <button type="button" style={btnPrimary} onClick={() => setCreating(true)}>Assign the first course</button> : undefined} />
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={headRow}>
              {["Course", "Assigned to", "Due", "Repeats", "Completion", "Progress", "Status"].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id} onClick={() => open(a.id)} style={{ borderBottom: "1px solid var(--c-border)", cursor: "pointer" }}>
                  <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>
                    <span style={{ ...text.ref, color: theme.color.faint, marginRight: 6 }}>{ref(a)}</span>{a.title}
                    <div style={{ ...text.meta, fontWeight: 400 }}>{labelOf(TRAINING_KINDS, a.kind)}{a.provider ? ` · ${a.provider}` : ""}</div>
                  </td>
                  <td style={td}>{a.audienceLabel}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDateOnly(a.dueAt)}</td>
                  <td style={td}>{recurrenceLabel(a.recurrenceMonths)}</td>
                  <td style={td}>{modeLabel(a.completionMode)}{a.passMark != null && <div style={text.meta}>pass mark {a.passMark}%</div>}</td>
                  <td style={{ ...td, minWidth: 150 }}>
                    <ProgressBar rate={a.progress.rate} />
                    <div style={{ ...text.meta, marginTop: 4 }}>{a.progress.completed} of {a.progress.expected}{a.progress.rate != null ? ` · ${a.progress.rate}%` : ""}</div>
                    {(a.progress.overdue > 0 || a.progress.awaitingConfirmation > 0) && (
                      <div style={{ ...text.meta, marginTop: 2 }}>
                        {a.progress.overdue > 0 && <span style={{ color: theme.color.dangerText }}>{a.progress.overdue} overdue</span>}
                        {a.progress.overdue > 0 && a.progress.awaitingConfirmation > 0 && " · "}
                        {a.progress.awaitingConfirmation > 0 && <span style={{ color: theme.color.accentText }}>{a.progress.awaitingConfirmation} to confirm</span>}
                      </div>
                    )}
                  </td>
                  <td style={td}><StatusPill value={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {creating && data && (
        <CreateModal data={data} proposal={fromProposal} onClose={() => { setCreating(false); setFromProposal(null); }}
          onDone={async (msg) => { setCreating(false); setFromProposal(null); setNotice(msg); await load(); }} />
      )}
      {openId && data && (selected
        ? <DetailModal assignment={selected} members={data.members} canEdit={canEdit} onClose={() => open(null)} onChanged={load} />
        : (
          <ModalOverlay onClose={() => open(null)}>
            <ModalHeader title="Training assignment" onClose={() => open(null)} />
            <div style={{ ...text.body, padding: 20, color: theme.color.muted }}>That assignment was not found. It may have been removed.</div>
          </ModalOverlay>
        ))}
    </div>
  );
}

// ── Audience ───────────────────────────────────────────────────────────────

interface AudienceValue { all: boolean; departmentIds: string[]; siteIds: string[]; userIds: string[] }

function AudiencePicker({ value, onChange, departments, sites, members }: { value: AudienceValue; onChange: (v: AudienceValue) => void; departments: Option[]; sites: Option[]; members: Option[] }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Check checked={value.all} onChange={(all) => onChange({ ...value, all })} label="Everyone" help="Every active member of the organisation." />
      {!value.all && (
        <>
          <Field label="Departments"><MultiPick options={departments} selected={value.departmentIds} onChange={(departmentIds) => onChange({ ...value, departmentIds })} empty="No departments set up" /></Field>
          <Field label="Sites"><MultiPick options={sites} selected={value.siteIds} onChange={(siteIds) => onChange({ ...value, siteIds })} empty="No sites set up" /></Field>
          <Field label="People"><MultiPick options={members} selected={value.userIds} onChange={(userIds) => onChange({ ...value, userIds })} empty="No active members" /></Field>
        </>
      )}
    </div>
  );
}

const audienceEmpty = (a: AudienceValue) => !a.all && a.departmentIds.length + a.siteIds.length + a.userIds.length === 0;
const plusDays = (n: number) => todayInput(n);

// ── Create ─────────────────────────────────────────────────────────────────

function CreateModal({ data, proposal, onClose, onDone }: { data: Overview; proposal?: AssignmentSeed | null; onClose: () => void; onDone: (msg: string) => Promise<void> }) {
  // Opened from a proposal (phishing results or a competence gap): its suggestion fills the form, every field still editable.
  const s = proposal?.suggested;
  const [title, setTitle] = useState(s?.title ?? "");
  const [kind, setKind] = useState(s?.kind ?? "security_awareness");
  const [provider, setProvider] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState(s?.description ?? "");
  const [due, setDue] = useState(plusDays(s?.dueInDays ?? 30));
  const [audience, setAudience] = useState<AudienceValue>(s?.audience ?? { all: true, departmentIds: [], siteIds: [], userIds: [] });
  const [recurrence, setRecurrence] = useState("");
  const [mode, setMode] = useState<"self_attest" | "admin_confirm">("self_attest");
  const [passMark, setPassMark] = useState("");
  const [autoEnrol, setAutoEnrol] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const memberOptions: Option[] = data.members.map((m) => ({ id: m.id, name: m.name }));
  const badUrl = url.trim() !== "" && !isHttps(url.trim());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (badUrl) { setError("The course link must start with https://"); return; }
    setSaving(true); setError(null);
    try {
      const dueAt = dateInputToIso(due, { endOfDay: true });
      const days = Math.max(1, Math.min(365, Math.round((new Date(dueAt ?? "").getTime() - Date.now()) / 86_400_000)));
      const res = await api.post<{ enrolled: number }>("/training-assignments", {
        title: title.trim(), kind, provider: provider.trim() || null, url: url.trim() || null, description: description.trim() || null,
        audience, dueAt, dueInDays: days, recurrenceMonths: recurrence ? Number(recurrence) : null,
        completionMode: mode, passMark: passMark.trim() ? Number(passMark) : null, autoEnrolJoiners: autoEnrol,
        competenceRequirementId: s?.competenceRequirementId ?? null,
      });
      await onDone(`Assigned to ${res.enrolled} ${res.enrolled === 1 ? "person" : "people"}.`);
    } catch (err) { setError(apiErrorMessage(err, "Could not assign the training")); setSaving(false); }
  }

  const disabled = saving || !title.trim() || !due || audienceEmpty(audience) || badUrl;

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={proposal ? proposal.heading : "Assign training"} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        {proposal && (
          <div style={{ ...text.hint, gridColumn: "1 / -1" }}>
            {proposal.note}
          </div>
        )}
        <Field label="Course *" span2><input required value={title} onChange={(e) => setTitle(e.target.value)} style={input} maxLength={200} placeholder="e.g. Annual security awareness 2026" /></Field>
        <Field label="Type *"><select value={kind} onChange={(e) => setKind(e.target.value)} style={input}>{TRAINING_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select></Field>
        <Field label="Provider"><input value={provider} onChange={(e) => setProvider(e.target.value)} style={input} maxLength={200} /></Field>
        <Field label="Course link" span2 help="Where people take the course. https only."><input type="url" value={url} onChange={(e) => setUrl(e.target.value)} style={input} placeholder="https://" /></Field>
        {badUrl && <div style={{ ...text.hint, gridColumn: "1 / -1", color: theme.color.dangerText, marginTop: -8 }}>The link must start with https://</div>}
        <Field label="Description" span2><textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} style={textarea} maxLength={2000} /></Field>
        <Field label="Due *"><input type="date" required value={due} min={plusDays(0)} onChange={(e) => setDue(e.target.value)} style={input} /></Field>
        <Field label="Repeats" help="A repeating course opens again before each completion expires.">
          <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} style={input}>
            <option value="">Once only</option>
            <option value="6">Every 6 months</option>
            <option value="12">Every 12 months</option>
            <option value="24">Every 24 months</option>
          </select>
        </Field>
        <Field label="How completion is recorded">
          <select value={mode} onChange={(e) => setMode(e.target.value as "self_attest" | "admin_confirm")} style={input}>
            <option value="self_attest">The person confirms it (self-attest)</option>
            <option value="admin_confirm">An administrator confirms it</option>
          </select>
        </Field>
        <Field label="Pass mark (%)" help="Optional. People must enter a score at or above it.">
          <input type="number" min={1} max={100} value={passMark} onChange={(e) => setPassMark(e.target.value)} style={input} />
        </Field>
        <Field label="Assign to *" span2>
          <AudiencePicker value={audience} onChange={setAudience} departments={data.departments} sites={data.sites} members={memberOptions} />
        </Field>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check checked={autoEnrol} onChange={setAutoEnrol} label="Add people who join later" help="New members who match this audience are assigned it when they join." />
        </div>
        {error && <div style={{ ...text.body, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={disabled} style={{ ...btnPrimary, opacity: disabled ? 0.6 : 1 }}>{saving ? "Assigning…" : "Assign"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Edit ───────────────────────────────────────────────────────────────────

/**
 * The course's own fields. Who it is assigned to, how completion is recorded and the pass mark
 * stay as set: changing them would change what earlier completions meant. Moving the due date
 * moves every still-pending first-cycle due date with it (the API does that).
 */
function EditModal({ assignment: a, onClose, onDone }: { assignment: Assignment; onClose: () => void; onDone: () => Promise<void> }) {
  const [title, setTitle] = useState(a.title);
  const [provider, setProvider] = useState(a.provider ?? "");
  const [url, setUrl] = useState(a.url ?? "");
  const [description, setDescription] = useState(a.description ?? "");
  const [due, setDue] = useState(toDateInput(a.dueAt));
  const [recurrence, setRecurrence] = useState(a.recurrenceMonths ? String(a.recurrenceMonths) : "");
  const [autoEnrol, setAutoEnrol] = useState(a.autoEnrolJoiners);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const badUrl = url.trim() !== "" && !isHttps(url.trim());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (badUrl) { setError("The course link must start with https://"); return; }
    setSaving(true); setError(null);
    try {
      await api.patch(`/training-assignments/${a.id}`, {
        title: title.trim(), provider: provider.trim() || null, url: url.trim() || null, description: description.trim() || null,
        // An unchanged date keeps its stored time; a new one runs to the end of that day, as on create.
        ...(due !== toDateInput(a.dueAt) ? { dueAt: dateInputToIso(due, { endOfDay: true }) } : {}),
        recurrenceMonths: recurrence ? Number(recurrence) : null, autoEnrolJoiners: autoEnrol,
      });
      await onDone();
    } catch (err) { setError(apiErrorMessage(err, "Could not save the assignment")); setSaving(false); }
  }

  const disabled = saving || !title.trim() || !due || badUrl;
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Edit ${ref(a)}`} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        <Field label="Course *" span2><input required value={title} onChange={(e) => setTitle(e.target.value)} style={input} maxLength={200} /></Field>
        <Field label="Provider"><input value={provider} onChange={(e) => setProvider(e.target.value)} style={input} maxLength={200} /></Field>
        <Field label="Due *"><input type="date" required value={due} onChange={(e) => setDue(e.target.value)} style={input} /></Field>
        <Field label="Course link" span2 help="Where people take the course. https only."><input type="url" value={url} onChange={(e) => setUrl(e.target.value)} style={input} placeholder="https://" /></Field>
        {badUrl && <div style={{ ...text.hint, gridColumn: "1 / -1", color: theme.color.dangerText, marginTop: -8 }}>The link must start with https://</div>}
        <Field label="Description" span2><textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} style={textarea} maxLength={2000} /></Field>
        <Field label="Repeats" help="A repeating course opens again before each completion expires.">
          <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)} style={input}>
            <option value="">Once only</option>
            {recurrence && !["6", "12", "24"].includes(recurrence) && <option value={recurrence}>Every {recurrence} months</option>}
            <option value="6">Every 6 months</option>
            <option value="12">Every 12 months</option>
            <option value="24">Every 24 months</option>
          </select>
        </Field>
        <div style={{ gridColumn: "1 / -1" }}>
          <Check checked={autoEnrol} onChange={setAutoEnrol} label="Add people who join later" help="New members who match this audience are assigned it when they join." />
        </div>
        {error && <div style={{ ...text.body, gridColumn: "1 / -1", color: theme.color.dangerText }}>{error}</div>}
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
          <button type="submit" disabled={disabled} style={{ ...btnPrimary, opacity: disabled ? 0.6 : 1 }}>{saving ? "Saving…" : "Save changes"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Detail ─────────────────────────────────────────────────────────────────

function DetailModal({ assignment: a, members, canEdit, onClose, onChanged }: { assignment: Assignment; members: Member[]; canEdit: boolean; onClose: () => void; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addIds, setAddIds] = useState<string[]>([]);
  const [personId, setPersonId] = useState("");

  async function act(fn: () => Promise<string | null>) {
    setBusy(true); setError(null); setNotice(null);
    try { const msg = await fn(); await onChanged(); if (msg) setNotice(msg); }
    catch (e) { setError(apiErrorMessage(e, "Could not update the assignment")); }
    setBusy(false);
  }

  const who = (x: Assignee) => x.user.displayName || x.user.email;
  const confirm = (x: Assignee) => act(async () => { await api.post(`/training-assignments/assignees/${x.id}`, { action: "confirm" }); return `Confirmed for ${who(x)}.`; });
  const reopen = (x: Assignee) => act(async () => { await api.post(`/training-assignments/assignees/${x.id}`, { action: "reopen" }); return `Reopened for ${who(x)}.`; });
  const excuse = (x: Assignee) => {
    const reason = window.prompt(`Why does ${who(x)} not need to complete this? (for example, long-term leave)`);
    if (reason == null) return;
    if (reason.trim().length < 3) { setError("Give a reason of at least three characters."); return; }
    void act(async () => { await api.post(`/training-assignments/assignees/${x.id}`, { action: "excuse", reason: reason.trim() }); return `${who(x)} excused.`; });
  };
  const setStatus = (status: "active" | "closed") => act(async () => { await api.patch(`/training-assignments/${a.id}`, { status }); return status === "closed" ? "Assignment closed." : "Assignment reopened."; });
  const addPeople = () => act(async () => {
    const r = await api.post<{ enrolled: number }>(`/training-assignments/${a.id}/assignees`, { userIds: addIds });
    setAdding(false); setAddIds([]);
    return r.enrolled ? `Added ${r.enrolled} ${r.enrolled === 1 ? "person" : "people"}.` : "Everyone chosen was already assigned it.";
  });

  const assigned = new Set(a.assignees.map((x) => x.userId));
  const addable: Option[] = members.filter((m) => !assigned.has(m.id)).map((m) => ({ id: m.id, name: m.name }));

  if (editing) {
    return <EditModal assignment={a} onClose={() => setEditing(false)} onDone={async () => { setEditing(false); await onChanged(); setNotice("Assignment saved."); }} />;
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={<><span style={{ ...text.ref, fontSize: "inherit", color: theme.color.faint, marginRight: 8 }}>{ref(a)}</span>{a.title}</>} onClose={onClose}
        right={<span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}><StatusPill value={a.status} />{canEdit && <EditButton onClick={() => setEditing(true)} />}</span>} />
      <div style={{ padding: "8px 20px 20px" }}>
        {/* ISO 27001 7.2 / A.6.3: what the training covers, how completion is shown, who still owes it. */}
        {a.status === "active" && (
          <NextSteps
            items={[
              { done: !!a.description?.trim(), label: "Describe what the course covers" },
              { done: isHttps(a.url) || !!a.provider?.trim(), label: "Give the course link or provider" },
              { done: a.recurrenceMonths != null, label: "Set how often it is repeated", hint: "Awareness training is usually renewed every year." },
              { done: a.assignees.length > 0, label: "Assign it to people" },
              ...(a.progress.awaitingConfirmation > 0 ? [{ done: false, label: `Confirm ${a.progress.awaitingConfirmation} completion${a.progress.awaitingConfirmation === 1 ? "" : "s"} waiting for you` }] : []),
              ...(a.progress.overdue > 0 ? [{ done: false, label: `Chase ${a.progress.overdue} ${a.progress.overdue === 1 ? "person" : "people"} past the due date`, urgent: true }] : []),
            ]}
            action={canEdit ? <button type="button" style={secondaryBtn} onClick={() => setEditing(true)}>Fill these in</button> : undefined}
          />
        )}
        <DetailSection title="Course">
          <DetailFields fields={[
            { label: "Type", value: labelOf(TRAINING_KINDS, a.kind) },
            { label: "Provider", value: a.provider },
            { label: "Course link", value: isHttps(a.url) ? <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-accentText)" }}>{a.url} <ExternalLink size={11} style={{ verticalAlign: -1 }} /></a> : null, wide: true },
            { label: "Description", value: a.description, wide: true, multiline: true },
          ]} />
        </DetailSection>
        <DetailSection title="Assignment">
          <DetailFields fields={[
            { label: "Assigned to", value: a.audienceLabel },
            { label: "People who join later", value: a.autoEnrolJoiners ? "Added automatically" : "Not added" },
            { label: "Due", value: fmtDateOnly(a.dueAt) },
            { label: "Repeats", value: recurrenceLabel(a.recurrenceMonths) },
            { label: "Completion", value: `${modeLabel(a.completionMode)}${a.passMark != null ? ` · pass mark ${a.passMark}%` : ""}` },
            { label: "Assigned", value: fmtDay(a.createdAt) },
            { label: "Closed", value: a.closedAt ? fmtDay(a.closedAt) : null },
          ]} />
        </DetailSection>
        <DetailSection title="Progress">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", ...text.body, color: theme.color.muted }}>
            <div style={{ flex: "1 1 200px", maxWidth: 320 }}><ProgressBar rate={a.progress.rate} /></div>
            <span>{a.progress.completed} of {a.progress.expected} completed{a.progress.rate != null ? ` (${a.progress.rate}%)` : ""} · {a.progress.overdue} overdue · {a.progress.awaitingConfirmation} to confirm</span>
          </div>
        </DetailSection>

        <DetailSection title="People">
        <div style={{ border: "1px solid var(--c-border)", borderRadius: 3, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={headRow}>{["Person", "Cycle", "Due", "Standing", "Score"].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {a.assignees.map((x) => {
                const pill = STANDING_PILL[x.standing] ?? STANDING_PILL.pending;
                return (
                  <tr key={x.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                    <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>
                      {who(x)}{!x.user.isActive && <span style={{ fontWeight: 400, color: theme.color.faint }}> (deactivated)</span>}
                      {x.user.displayName && <div style={{ ...text.meta, fontWeight: 400 }}>{x.user.email}</div>}
                      {x.attestation && <div style={{ ...text.meta, fontWeight: 400, marginTop: 2 }}>Note: {x.attestation}</div>}
                    </td>
                    <td style={td}>{x.cycle}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDateOnly(x.dueAt)}</td>
                    <td style={td}>
                      {pill.status ? <StatusTag tone={pill.status} title={x.excusedReason ?? undefined}>{pill.label}</StatusTag> : <Pill tone={pill.tone} title={x.excusedReason ?? undefined}>{pill.label}</Pill>}
                      {x.status === "completed" && x.completedAt && <div style={{ ...text.meta, marginTop: 3 }}>{fmtDay(x.completedAt)}</div>}
                      {x.status === "submitted" && x.submittedAt && <div style={{ ...text.meta, marginTop: 3 }}>recorded {fmtDay(x.submittedAt)}</div>}
                      {x.status === "excused" && x.excusedReason && <div style={{ ...text.meta, marginTop: 3 }}>{x.excusedReason}</div>}
                    </td>
                    <td style={td}>{x.score != null ? `${x.score}%` : "—"}</td>
                  </tr>
                );
              })}
              {a.assignees.length === 0 && <tr><td colSpan={5} style={{ ...td, padding: 20 }}>Nobody is assigned this course.</td></tr>}
            </tbody>
          </table>
        </div>
        </DetailSection>

        {error && <div style={{ ...text.body, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}
        {notice && <div style={{ ...text.body, color: theme.color.successText, marginBottom: 8 }}>{notice}</div>}

        {canEdit && (() => {
          const actionable = a.assignees.filter((x) => x.status !== "completed");
          const x = actionable.find((y) => y.id === personId) ?? null;
          const toConfirm = a.assignees.filter((y) => y.status === "submitted").length;
          return (
            <RecordActions note={toConfirm > 0 ? `${toConfirm} completion${toConfirm === 1 ? "" : "s"} to confirm: choose the person, then Confirm.` : undefined}>
              {actionable.length > 0 && (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <select value={personId} onChange={(e) => setPersonId(e.target.value)} style={{ ...input, width: "auto", maxWidth: 260 }} aria-label="Person">
                    <option value="">Choose a person…</option>
                    {actionable.map((y) => <option key={y.id} value={y.id}>{who(y)} · {(STANDING_PILL[y.standing] ?? STANDING_PILL.pending).label}</option>)}
                  </select>
                  {x && x.status === "submitted" && <button type="button" disabled={busy} style={{ ...btnPrimary, padding: "6px 12px", fontSize: text.hint.fontSize }} onClick={() => { void confirm(x); setPersonId(""); }}>Confirm</button>}
                  {x && (x.status === "pending" || x.status === "submitted") && <button type="button" disabled={busy} style={secondaryBtn} onClick={() => { excuse(x); setPersonId(""); }}>Excuse</button>}
                  {x && (x.status === "submitted" || x.status === "excused") && <button type="button" disabled={busy} style={secondaryBtn} onClick={() => { void reopen(x); setPersonId(""); }}>Reopen for them</button>}
                </span>
              )}
              {a.status === "active" && <button type="button" disabled={busy} style={secondaryBtn} onClick={() => setAdding((v) => !v)}>Add people</button>}
              {a.status === "active"
                ? <button type="button" disabled={busy} style={dangerBtn} onClick={() => { if (window.confirm("Close this assignment? Nobody can record it any more and no further cycles open.")) void setStatus("closed"); }}>Close assignment</button>
                : <button type="button" disabled={busy} style={secondaryBtn} onClick={() => setStatus("active")}>Reopen assignment</button>}
              {adding && (
                <div style={{ display: "grid", gap: 10, padding: 14, border: "1px solid var(--c-border)", borderRadius: 3, width: "100%" }}>
                  <MultiPick options={addable} selected={addIds} onChange={setAddIds} empty="Every active member is already assigned it" />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" disabled={busy || addIds.length === 0} style={{ ...btnPrimary, opacity: busy || addIds.length === 0 ? 0.6 : 1 }} onClick={addPeople}>Add {addIds.length || ""}</button>
                    <button type="button" style={secondaryBtn} onClick={() => { setAdding(false); setAddIds([]); }}>Cancel</button>
                  </div>
                </div>
              )}
            </RecordActions>
          );
        })()}
      </div>
    </ModalOverlay>
  );
}
